import { debounce } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';

import { isModalOpen } from 'src/utils/is-modal-open';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';
import Search from 'src/components/search/Search';
import { useConversationsPageContext } from 'src/pages/landlord/conversations/context';
import { isMac, isMobileUser } from 'src/common/utils';

const HOTKEY = isMac() ? '⌘K' : 'Ctrl+K';

type Props = {
    searchScope: 'inbox' | 'all';
}

const getDesktopPlaceholder = (scope: 'inbox' | 'all') => {
    if (scope === 'inbox') {
        return 'Search inbox by lead name, email, phone, UUID';
    } else {
        return 'Search by message content, lead name, email, phone number';
    }
};

export default function SearchInput({ searchScope }: Props) {
    const { search, setSearch } = useConversationsPageContext();
    const { width: windowWidth } = useWindowSize();

    const inputRef = useRef<HTMLInputElement>(null);

    const [value, setValue] = useState(search);
    const [isSearching, setSearching] = useState(false);

    const setSearchQueryDebounced = useCallback(debounce(setSearch, 300), []);

    useEffect(() => {
        setSearchQueryDebounced(value);
    }, [value, setSearchQueryDebounced]);

    useEffect(() => {
        if (value !== search) {
            setSearching(true);
        } else {
            setSearching(false);
        }
    }, [value, search]);

    useFocusOnCtrlK(inputRef as React.RefObject<HTMLInputElement>);

    return (
        <Search
            inputRef={inputRef}
            placeholder={isMobileUser() ? 'Search conversations' : getDesktopPlaceholder(searchScope)}
            value={value}
            onChange={setValue}
            isLoading={isSearching}
            sx={{ width: windowWidth >= 1280 ? 600 : 400, maxWidth: '100%', minWidth: '30%', bgcolor: 'common.white' }}
            data-testid="conversations-search"
            hotkey={HOTKEY}
        />
    );
}

// focus on input when 'Ctrl+K' key is pressed
const useFocusOnCtrlK = (inputRef: React.RefObject<HTMLInputElement>) => {
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const isModifierPressed = isMac() ? event.metaKey : event.ctrlKey;
            if (event.key === 'k' && isModifierPressed) {
                if (isModalOpen()) {
                    return;
                }

                const keyLabel = isMac() ? '⌘K' : 'Ctrl+K';
                analyticsClient.track(AnalyticsEvent.SHORTCUT_KEY_USED, {
                    page: 'Inbox',
                    key: keyLabel,
                });

                event.preventDefault();
                inputRef.current?.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);
};
